import { mergeAttributes } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import OrderedList from "@tiptap/extension-ordered-list";
import cn from "classnames";

import "./style.scss";

import { LIST_STYLE_TYPES, nextListStyleType } from "./util";

const listStyleInheritKey = new PluginKey("orderedListStyleInherit");

export default OrderedList.extend({
    renderHTML({ HTMLAttributes }) {
        delete HTMLAttributes.start;

        return ["ol", mergeAttributes(this.options.HTMLAttributes, HTMLAttributes), 0];
    },
    // A freshly nested list continues its ancestor's numbering ladder (the
    // attribute defaults to null); once resolved it is a concrete value the
    // user can override per level via the toolbar dropdown.
    addProseMirrorPlugins() {
        const { name } = this;

        return [
            ...(this.parent?.() ?? []),
            new Plugin({
                key: listStyleInheritKey,
                appendTransaction: (transactions, _oldState, newState) => {
                    if (!transactions.some((tr) => tr.docChanged)) {
                        return null;
                    }

                    const updates = [];
                    newState.doc.descendants((node, pos) => {
                        if (node.type.name !== name || node.attrs.listStyleType != null) {
                            return;
                        }

                        // Distance counts every ordered list in between, so levels
                        // still waiting for their own value in this same pass do
                        // not throw the ladder off.
                        const $pos = newState.doc.resolve(pos);
                        let distance = 0;
                        for (let depth = $pos.depth; depth > 0; depth--) {
                            const ancestor = $pos.node(depth);
                            if (ancestor.type.name !== name) {
                                continue;
                            }

                            distance += 1;
                            if (ancestor.attrs.listStyleType) {
                                updates.push({
                                    pos,
                                    listStyleType: nextListStyleType(
                                        ancestor.attrs.listStyleType,
                                        distance,
                                    ),
                                });
                                break;
                            }
                        }
                    });

                    if (updates.length === 0) {
                        return null;
                    }

                    const { tr } = newState;
                    updates.forEach(({ pos, listStyleType }) => {
                        const node = tr.doc.nodeAt(pos);
                        if (node) {
                            tr.setNodeMarkup(pos, undefined, { ...node.attrs, listStyleType });
                        }
                    });

                    return tr.setMeta("addToHistory", false);
                },
            }),
        ];
    },
    addCommands() {
        const type = this.name;

        // Depth-based position of the nearest ancestor of `typeName` for a resolved
        // position, or null when there is none.
        const nearest = ($pos, typeName) => {
            for (let depth = $pos.depth; depth > 0; depth--) {
                if ($pos.node(depth).type.name === typeName) {
                    return $pos.before(depth);
                }
            }
            return null;
        };

        return {
            ...this.parent?.(),
            setListStyleType: (listStyleType) => ({ state, tr, dispatch }) => {
                if (!LIST_STYLE_TYPES.includes(listStyleType)) {
                    return false;
                }

                const { $from, $to, empty } = state.selection;
                const fromItem = nearest($from, "listItem");
                const toItem = nearest($to, "listItem");
                // A selection that stays inside a single list item (or a bare
                // cursor) only restyles the current level; a selection that spans
                // items/levels restyles every ordered list it touches.
                const localToOneLevel = empty || (fromItem != null && fromItem === toItem);

                const positions = [];
                if (localToOneLevel) {
                    const pos = nearest($from, type);
                    if (pos == null) {
                        return false;
                    }
                    positions.push(pos);
                } else {
                    state.doc.nodesBetween($from.pos, $to.pos, (node, pos) => {
                        if (node.type.name === type) {
                            positions.push(pos);
                        }
                    });
                }

                if (positions.length === 0) {
                    return false;
                }

                positions.forEach((pos) => {
                    const node = tr.doc.nodeAt(pos);
                    if (node) {
                        tr.setNodeMarkup(pos, undefined, { ...node.attrs, listStyleType });
                    }
                });

                if (dispatch) {
                    dispatch(tr);
                }

                return true;
            },
        };
    },
    addAttributes() {
        return {
            ...this.parent?.(),
            // The numbering scheme for this list level. Rendered both as an inline
            // `list-style-type` (native fallback / copy-paste) and a data attribute
            // that the editor CSS keys off to draw the ::before markers.
            listStyleType: {
                // null = inherit from the parent list (resolved by the plugin above);
                // a concrete value is an explicit per-level choice.
                default: null,
                parseHTML: (element) => {
                    const value =
                        element.getAttribute("data-list-style-type") || element.style.listStyleType;

                    return LIST_STYLE_TYPES.includes(value) ? value : null;
                },
                renderHTML: (attributes) => {
                    const value = attributes.listStyleType;

                    // No value at all keeps the legacy nested "1.1." numbering;
                    // an explicit decimal is a level of the ladder, so it is
                    // rendered as a standalone marker like the other schemes.
                    if (!value) {
                        return {};
                    }

                    return {
                        "data-list-style-type": value,
                        style: `list-style-type: ${value}`,
                    };
                },
            },
            helpText: {
                default: false,
                parseHTML: (element) => element.classList.contains("help-text"),
            },
            hasComment: {
                default: false,
            },
            class: {
                default: undefined,
                renderHTML: (attributes) => {
                    return {
                        class: cn({
                            "help-text": attributes.helpText,
                            "has-comment": attributes.hasComment,
                        }),
                    };
                },
            },
            "data-diff-node": {
                default: undefined,
                renderHTML: (attributes) => {
                    return {
                        class: cn({
                            inserted: attributes["data-diff-node"] === "inserted",
                            deleted: attributes["data-diff-node"] === "deleted",
                            changed: attributes["data-diff-node"] === "changed",
                        }),
                    };
                },
            },
            "data-diff-id": {
                default: undefined,
            },
        };
    },
});
